import { fmtNum } from '../ui/ListFoot';
import type { LibraryEvent, BookData, Copy, CopyFormat, Edition, HistoryRow, Loan, Precision, Read, Status } from './types';
import { ownCover } from '../queue/lookups';
import { lineWords, loanState, nowLine, stateWords, whenWords } from '../lib/libraryStates';

// The book page's rules, as plain functions (tested in tests/logic/book-model.test.ts).
// Decisions: site-redesign-decisions.md › "From the build: decisions with a screen side
// (Kristi, Oct 26, the book page)" and the Oct 20 book page notes.

// ---------- dates ----------

const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Today" as YYYY-MM-DD in a time zone (Pacific until Settings exist). */
export function todayIn(timeZone = 'America/Los_Angeles', now = new Date()): string {
  const p = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  const v = (t: string) => p.find((x) => x.type === t)!.value;
  return `${v('year')}-${v('month')}-${v('day')}`;
}

function parts(iso: string): [number, number, number] {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return [y ?? 0, m ?? 1, d ?? 1];
}

/** Days from `a` to `b` (both YYYY-MM-DD), by calendar day. */
export function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = parts(a);
  const [by, bm, bd] = parts(b);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000);
}

export function addDays(iso: string, n: number): string {
  const [y, m, d] = parts(iso);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/**
 * The app's one date format (consistency pass, R8 W3): "Oct 6" this year, "Oct 6, 2025" in any
 * other year. It always knows today (Pacific), so no screen can forget to pass it.
 */
export function dayText(iso: string | null | undefined, today: string = todayIn()): string {
  if (!iso) return '';
  const [y, m, d] = parts(iso);
  const sameYear = parts(today)[0] === y;
  return sameYear ? `${MONTHS[m - 1]} ${d}` : `${MONTHS[m - 1]} ${d}, ${y}`;
}

/**
 * A fuzzy date as written: your own words win ("about 2008, while wrapping up my MSIM");
 * otherwise by its precision: day "Oct 6, 2026" · month "Oct 2026" · year "2026" · about "about 2008".
 */
export function fuzzyText(iso: string | null, precision: Precision | null, words?: string | null, today: string = todayIn()): string {
  if (words && words.trim()) return words.trim();
  if (!iso) return '';
  const [y, m] = parts(iso);
  switch (precision) {
    case 'month':
      return `${MONTHS[m - 1]} ${y}`;
    case 'year':
      return String(y);
    case 'about':
      return `about ${y}`;
    default:
      return dayText(iso, today);
  }
}

/** A month heading: "October 2026" (R8 W3: full names only in headings). */
export function monthHeading(iso: string): string {
  const [y, m] = parts(iso);
  return `${MONTHS_FULL[m - 1]} ${y}`;
}

/** A month on its own, always with its year: "Oct 2026". */
export function monthText(iso: string): string {
  const [y, m] = parts(iso);
  return `${MONTHS[m - 1]} ${y}`;
}

export function timeText(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return h ? (m ? `${h} h ${m} min` : `${h} h`) : `${m} min`;
}

// ---------- names ----------

export const STATUS_NAMES: Record<Status, string> = {
  currently_reading: 'Currently Reading',
  up_next: 'Up Next',
  future_reads: 'Future reads',
  finished: 'Finished',
  not_for_me: 'Not for me',
  reference: 'Reference',
};

export const statusName = (s: Status | null) => (s ? STATUS_NAMES[s] : 'No shelf yet');

export const bookLabel = (n: number) => `B${String(n).padStart(4, '0')}`;

/** "/book/B0443" → 443; also "B443" and "443". */
export function labelFromId(id: string): number | null {
  const m = id.match(/^b?0*(\d{1,6})$/i);
  return m ? Number(m[1]) : null;
}

const FORMAT_NAMES: Record<CopyFormat, string> = {
  kindle: 'Kindle',
  audible: 'Audible',
  paper: 'Paper',
  apple_books: 'Apple Books',
  google_play: 'Google Play',
  kobo: 'Kobo',
  librofm: 'Libro.fm',
  libby: 'Libby',
  hoopla: 'Hoopla',
  pdf_epub: 'PDF / EPUB',
  article: 'Article',
  other: 'Other',
};

const HOW_NAMES = { bought: 'Owned', gift: 'Gift', shared: 'Shared', free: 'Free', library_loan: 'Library' } as const;

export function editionOf(book: Pick<BookData, 'editions'>, copy: Copy): Edition | undefined {
  return copy.edition_id ? book.editions.find((e) => e.id === copy.edition_id) : undefined;
}

/** Listen or read: an audiobook edition, or Audible / Libro.fm / Libby (the import's "Libby audio") with no edition. */
export function isListen(copy: Copy, edition: Edition | undefined): boolean {
  if (edition) return edition.kind === 'audiobook';
  return copy.format === 'audible' || copy.format === 'librofm' || copy.format === 'libby';
}

/** "Kindle", "Audible", "Libby audio", "Paper"… */
export function formatName(copy: Copy, edition: Edition | undefined): string {
  if (copy.format === 'libby') return isListen(copy, edition) ? 'Libby audio' : 'Libby';
  return FORMAT_NAMES[copy.format];
}

/** "Kindle · Library", "Audible · Owned". */
export function copyName(book: Pick<BookData, 'editions'>, copy: Copy): string {
  return `${formatName(copy, editionOf(book, copy))} · ${HOW_NAMES[copy.how]}`;
}

/**
 * The copy card's facts line (0039): what the published edition is, skipping what isn't known.
 * "Easton Press · The 100 Greatest Books Ever Written · Collector's edition · 1977 · 652 pages".
 * The copy's own pages or length win over the edition's. Empty when nothing is known.
 */
export function editionFacts(book: Pick<BookData, 'editions'>, copy: Copy): string {
  const e = editionOf(book, copy);
  const listen = isListen(copy, e);
  const minutes = copy.listening_minutes ?? e?.listening_minutes ?? null;
  const pages = copy.pages ?? e?.pages ?? null;
  const size = listen ? (minutes ? timeText(minutes) : null) : pages ? `${fmtNum(pages)} pages` : null;
  return [e?.publisher, e?.imprint, e?.edition_statement, e ? fuzzyText(e.published_on, e.published_precision) : '', size]
    .map((x) => (x ?? '').trim())
    .filter(Boolean)
    .join(' · ');
}

/** "No. 412 of 1,000" · "One of 1,000" · "No. 412" · "" */
export function numberedText(n: number | null, total: number | null): string {
  const f = fmtNum;
  if (n && total) return `No. ${f(n)} of ${f(total)}`;
  if (total) return `One of ${f(total)}`;
  if (n) return `No. ${f(n)}`;
  return '';
}

/** The second line, only what's true of your own copy: "Signed · No. 412 of 1,000". Empty when nothing is set. */
export function copyFacts(copy: Pick<Copy, 'signed' | 'number_in_run' | 'run_size'>): string {
  return [copy.signed ? 'Signed' : '', numberedText(copy.number_in_run, copy.run_size)].filter(Boolean).join(' · ');
}

/** The Kindle ID (ASIN) in a pasted Kindle link: ?asin=…, /dp/…, /gp/product/…, /product/…; '' when none. */
export function asinInLink(url: string | null | undefined): string {
  if (!url) return '';
  const m = /[?&]asin=([A-Z0-9]{10})(?![A-Z0-9])/i.exec(url) ?? /\/(?:dp|gp\/product|product)\/([A-Z0-9]{10})(?![A-Z0-9])/i.exec(url);
  return m ? m[1]!.toUpperCase() : '';
}

// ---------- reads ----------

/** Reads in the order they happened: by start (undated first, as older), then as recorded. */
export function readsInOrder(reads: Read[]): Read[] {
  return [...reads].sort((a, b) => {
    if (!a.started_on && b.started_on) return -1;
    if (a.started_on && !b.started_on) return 1;
    if (a.started_on && b.started_on && a.started_on !== b.started_on) return a.started_on < b.started_on ? -1 : 1;
    return a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : 0;
  });
}

export const openRead = (book: Pick<BookData, 'reads'>) => book.reads.find((r) => r.outcome === 'open');

/** The read Reading as, progress and Open in follow: the open read, else the latest. */
export function currentRead(book: Pick<BookData, 'reads'>): Read | undefined {
  return openRead(book) ?? readsInOrder(book.reads).at(-1);
}

/** Copies a read still uses (not marked Stopped using this). */
export function copiesInUse(book: Pick<BookData, 'copies'>, read: Read | undefined): Copy[] {
  if (!read) return [];
  const ids = new Set(read.copies.filter((c) => !c.stopped_using_on).map((c) => c.copy_id));
  return book.copies.filter((c) => ids.has(c.id));
}

export type ReadingAs = 'Read' | 'Listen' | 'Read & Listen';

/**
 * Reading as (Kristi, Oct 26): worked out from the read's copies unless you set it.
 * `set` says which: "from your copies" or "set by you".
 */
export function readingAs(book: Pick<BookData, 'copies' | 'editions'>, read: Read | undefined): { as: ReadingAs; set: boolean } | null {
  if (!read) return null;
  const named = { read: 'Read', listen: 'Listen', both: 'Read & Listen' } as const;
  if (read.reading_as) return { as: named[read.reading_as], set: true };
  const copies = copiesInUse(book, read);
  if (!copies.length) return null;
  const listens = copies.map((c) => isListen(c, editionOf(book, c)));
  const as: ReadingAs = listens.every(Boolean) ? 'Listen' : listens.some(Boolean) ? 'Read & Listen' : 'Read';
  return { as, set: false };
}

export function latestProgress(read: Read | undefined): number | null {
  if (!read) return null;
  if (read.outcome === 'finished') return 100;
  if (read.outcome === 'stopped') return read.stopped_percent;
  const p = [...read.progress].sort((a, b) => (a.noted_at < b.noted_at ? 1 : -1))[0];
  return p ? p.percent : read.started_at_percent;
}

const pagesOf = (book: Pick<BookData, 'editions'>, c: Copy) => c.pages ?? editionOf(book, c)?.pages ?? null;
const minutesOf = (book: Pick<BookData, 'editions'>, c: Copy) => c.listening_minutes ?? editionOf(book, c)?.listening_minutes ?? null;

/** One copy's point at a %: "about page 121 of 448" or "about 4 h 3 min of 15 h". */
export function positionOn(book: Pick<BookData, 'editions'>, copy: Copy, pct: number): string | null {
  if (isListen(copy, editionOf(book, copy))) {
    const m = minutesOf(book, copy);
    return m ? `about ${timeText((m * pct) / 100)} of ${timeText(m)}` : null;
  }
  const p = pagesOf(book, copy);
  return p ? `about page ${Math.round((p * pct) / 100)} of ${p}` : null;
}

/** The Progress box line: the read's copies in their own units, joined. */
export function positionLine(book: Pick<BookData, 'copies' | 'editions' | 'reads'>, read: Read | undefined, pct: number | null): string | null {
  if (!read || pct === null) return null;
  const seen = new Set<string>();
  const out: string[] = [];
  const inUse = new Set(copiesInUse(book, read).map((c) => c.id));
  for (const c of copiesInOrder(book).filter((x) => inUse.has(x.id))) {
    const p = positionOn(book, c, pct);
    if (p && !seen.has(p)) {
      seen.add(p);
      out.push(out.length ? p : p[0]!.toUpperCase() + p.slice(1));
    }
  }
  return out.length ? out.join(' · ') : null;
}

/** "★4", "★3.5". */
export const starsShort = (r: number) => `★${Number.isInteger(r) ? r : r.toFixed(1)}`;

/** "★★★★½" for a read's row. */
export const starsFull = (r: number) => '★'.repeat(Math.floor(r)) + (r % 1 ? '½' : '');

/**
 * A book's rating with several reads (Kristi, Oct 26): the latest rated read, then earlier
 * ones newest first ("★4 · before ★3.5"); after two earlier ones, "+n more". Never the average.
 */
export function ratingLine(reads: Read[]): string | null {
  const rated = reads
    .filter((r) => r.rating !== null)
    .sort((a, b) => {
      const ka = a.ended_on ?? a.started_on ?? '';
      const kb = b.ended_on ?? b.started_on ?? '';
      if (ka !== kb) return ka < kb ? 1 : -1;
      return a.created_at < b.created_at ? 1 : -1;
    });
  if (!rated.length) return null;
  const [latest, ...earlier] = rated;
  let line = starsShort(latest!.rating!);
  if (earlier.length) line += ` · before ${earlier.slice(0, 2).map((r) => starsShort(r.rating!)).join(', ')}`;
  if (earlier.length > 2) line += ` · +${earlier.length - 2} more`;
  return line;
}

/** Tries: stopped reads since the last finished one (decisions doc › Try again). */
export function triesSinceFinish(reads: Read[]): { tries: number; lastStoppedAt: number | null } {
  const ordered = readsInOrder(reads);
  let tries = 0;
  let last: number | null = null;
  for (const r of ordered) {
    if (r.outcome === 'finished') {
      tries = 0;
      last = null;
    } else if (r.outcome === 'stopped') {
      tries++;
      last = r.stopped_percent;
    }
  }
  return { tries, lastStoppedAt: last };
}

export function openPause(read: Read | undefined) {
  return read?.outcome === 'open' ? read.pauses.find((p) => !p.resumed_on) : undefined;
}

/** "Mar 3 – Mar 30, 2024", "Started Oct 6, 2026", "about 2018–2019". */
export function readWhen(read: Read): string {
  const start = fuzzyText(read.started_on, read.started_precision, read.started_words);
  const end = fuzzyText(read.ended_on, read.ended_precision, read.ended_words);
  if (read.outcome === 'open') return start ? `Started ${start}` : 'Started, date not recorded';
  if (start && end) {
    if (read.started_precision === 'day' && read.ended_precision === 'day' && read.started_on && read.ended_on) {
      const sameYear = read.started_on.slice(0, 4) === read.ended_on.slice(0, 4);
      return `${sameYear ? dayText(read.started_on, read.ended_on) : dayText(read.started_on)} – ${dayText(read.ended_on)}`;
    }
    return `${start} – ${end}`;
  }
  if (end) return read.outcome === 'stopped' ? `Stopped ${end}` : `Finished ${end}`;
  if (start) return `Started ${start}`;
  return 'Date not recorded';
}

export function readState(read: Read): string {
  if (read.outcome === 'open') return openPause(read) ? 'Paused' : 'Reading now';
  if (read.outcome === 'finished') return read.rating !== null ? `Finished · ${starsFull(read.rating)}` : 'Finished';
  const at = read.stopped_percent !== null ? ` at ${Math.round(read.stopped_percent)}%` : '';
  return `Stopped${at}`;
}

// ---------- copies and loans ----------

/** The loan that describes a library copy now: the latest one. */
export function currentLoan(copy: Copy): Loan | undefined {
  return [...copy.loans].sort((a, b) => {
    const ka = a.hold_placed_on ?? a.borrowed_on ?? a.created_at;
    const kb = b.hold_placed_on ?? b.borrowed_on ?? b.created_at;
    return ka < kb ? 1 : ka > kb ? -1 : a.created_at < b.created_at ? 1 : -1;
  })[0];
}

const isLibrary = (c: Copy) => c.how === 'library_loan';
const isBorrowed = (c: Copy) => isLibrary(c) && currentLoan(c)?.step === 'borrowed';
const isWaiting = (c: Copy) => isLibrary(c) && (currentLoan(c)?.step === 'on_hold' || currentLoan(c)?.step === 'ready');
const isOwnNow = (c: Copy) => !isLibrary(c) && c.state === 'have_it';

/** Can you read this copy now? Your own, or a library copy while it's borrowed. */
export const readableNow = (c: Copy) => isOwnNow(c) || isBorrowed(c);

/**
 * Order of copies in Copies & formats (decided Oct 26): (1) borrowed library copies, soonest
 * due first; (2) your own copies, the current read's first, then the most recently acquired;
 * (3) library holds still waiting; (4) returned library copies, then lent out, lost, donated.
 */
export function copiesInOrder(book: Pick<BookData, 'copies' | 'reads'>): Copy[] {
  const read = currentRead(book);
  const onRead = new Set(read ? read.copies.filter((c) => !c.stopped_using_on).map((c) => c.copy_id) : []);
  const group = (c: Copy) => {
    if (isBorrowed(c)) return 1;
    if (isOwnNow(c)) return 2;
    if (isWaiting(c)) return 3;
    if (isLibrary(c)) return 4;
    return { have_it: 2, lent_out: 5, lost: 6, donated: 7, returned: 4 }[c.state];
  };
  return [...book.copies].sort((a, b) => {
    const ga = group(a);
    const gb = group(b);
    if (ga !== gb) return ga - gb;
    if (ga === 1) return (currentLoan(a)?.due_on ?? '9999') < (currentLoan(b)?.due_on ?? '9999') ? -1 : 1;
    if (ga === 2) {
      const ra = onRead.has(a.id) ? 0 : 1;
      const rb = onRead.has(b.id) ? 0 : 1;
      if (ra !== rb) return ra - rb;
      const ka = a.acquired_on ?? '';
      const kb = b.acquired_on ?? '';
      if (ka !== kb) return ka < kb ? 1 : -1;
    }
    return 0;
  });
}

const plural = (n: number, one: string, many = one + 's') => `${n} ${n === 1 ? one : many}`;

/**
 * The library copy's line on the book page: its library state's words, as a card pill and a Library
 * copies row say them (consistency pass R2, L5, Kristi Nov 2): "Loan ended Oct 28", "Ready · borrow
 * by Nov 3", "Due Nov 4", "Due today", "Check Libby · expected Nov 6", "Not started · due Nov 19",
 * "On hold · expected Jan 13, 2027 · #3 in line · 2 copies". `reading`: the book is on Currently
 * Reading (as Shelves and Library copies decide "not started").
 */
export function loanLine(loan: Loan, today: string, reading = false, borrowWindow = 3): string {
  const state = loanState({ step: loan.step, dueOn: loan.due_on, expectedOn: loan.expected_on, checkDays: loan.check_days, quietUntil: loan.quiet_until ?? null }, reading, today);
  if (state) {
    const borrowBy = loan.borrow_by ?? (loan.ready_on ? addDays(loan.ready_on, borrowWindow) : null);
    const dates = { dueOn: loan.due_on, borrowBy, expectedOn: loan.expected_on };
    const words = [stateWords(state, dates, today), whenWords(state, dates, today)].filter(Boolean).join(' · ');
    if (loan.step !== 'on_hold') return words;
    return [words, lineWords(loan.place_in_line, loan.library_copies), loan.hold_placed_on ? `placed ${dayText(loan.hold_placed_on, today)}` : '', loan.expected_on ? '' : 'no estimate yet']
      .filter(Boolean)
      .join(' · ');
  }
  if (loan.step === 'returned') return loan.returned_on ? `Returned ${dayText(loan.returned_on, today)}` : 'Returned';
  return loan.cancelled_on ? `Hold cancelled ${dayText(loan.cancelled_on, today)}` : 'Hold cancelled';
}

export const EVENT_NAMES: Record<string, string> = {
  hold_placed: 'Hold placed',
  new_expected_date: 'New expected date',
  ready: 'Ready to borrow',
  deliver_later: 'Deliver later',
  let_go: 'Let it go',
  borrowed: 'Borrowed',
  renewed: 'Renewed',
  started_reading: 'Started reading',
  finished: 'Finished',
  didnt_finish: 'Didn’t finish',
  returned: 'Returned',
  returned_unread: 'Returned unread',
  switched_to_own_copy: 'Switched to my own copy',
  cancelled: 'Hold cancelled',
  other: 'Other',
};

export interface LibraryStep {
  on: string | null;
  /** The step's name, shown as a pill (board 4): "Borrowed", "Didn’t finish". */
  name: string;
  /** What else it says: "due Oct 27", "at 12%". */
  detail: string;
  /** Pill tone: ladder steps on the teal tint, others quiet. */
  tone: 'teal' | 'quiet';
  copy: string;
  /** The library_events row (edit mode: Change the date · This didn't happen). */
  event?: LibraryEvent;
  copyId: string;
}

export const TEAL_STEPS = new Set(['borrowed', 'ready', 'didnt_finish', 'finished', 'renewed', 'started_reading']);

/**
 * This book's library history: every step of every loan, newest first, and its summary line.
 * With `copyId`, only that library copy's loans (the history sits inside its card, board 4).
 */
export function libraryHistory(
  book: Pick<BookData, 'copies' | 'editions'>,
  today: string,
  copyId?: string,
): { summary: string; steps: LibraryStep[] } | null {
  const loans = book.copies
    .filter(isLibrary)
    .filter((c) => !copyId || c.id === copyId)
    .flatMap((c) => c.loans.map((l) => ({ c, l })));
  if (!loans.length) return null;
  const steps: LibraryStep[] = [];
  for (const { c, l } of loans) {
    for (const e of l.events) {
      const detail: string[] = [];
      if (e.kind === 'borrowed' && e.due_on) detail.push(`due ${dayText(e.due_on, today)}`);
      if (e.kind === 'didnt_finish' && e.percent !== null) detail.push(`at ${Math.round(e.percent)}%`);
      steps.push({
        on: e.happened_on,
        name: EVENT_NAMES[e.kind] ?? e.kind,
        detail: detail.join(' · '),
        tone: TEAL_STEPS.has(e.kind) ? 'teal' : 'quiet',
        copy: formatName(c, editionOf(book, c)),
        copyId: c.id,
        event: e,
      });
    }
  }
  steps.sort((a, b) => ((a.on ?? '') < (b.on ?? '') ? 1 : (a.on ?? '') > (b.on ?? '') ? -1 : 0));
  const borrowed = loans.filter(({ l }) => l.events.some((e) => e.kind === 'borrowed') || l.borrowed_on || l.step === 'borrowed' || l.step === 'returned').length;
  const ranOut = loans.filter(({ l }) => l.events.some((e) => e.kind === 'didnt_finish')).length;
  const bits: string[] = [];
  if (borrowed) bits.push(`Borrowed ${borrowed === 1 ? 'once' : `${borrowed} times`}`);
  if (ranOut) bits.push(`ran out ${ranOut === 1 ? 'once' : `${ranOut} times`}`);
  // Where it stands now, as Library copies' history says it (R2, L5): "Now: borrowed, due Nov 19".
  const now = nowLine(loans.map(({ l }) => ({ step: l.step, dueOn: l.due_on })), today);
  if (now) bits.push(now);
  if (!bits.length) bits.push(plural(loans.length, 'hold'));
  return { summary: bits.join(' · '), steps };
}

/** The library card's badge (board 4): where the loan stands, "Reading" while it's on the open read. */
export function loanBadge(loan: Loan, onOpenRead: boolean): string {
  if (loan.step === 'borrowed') return onOpenRead ? 'Reading' : 'Borrowed';
  // "Ready", as rows say; the line under it says "borrow by" (Kristi, Nov 2).
  return { on_hold: 'On hold', ready: 'Ready', returned: 'Returned', cancelled: 'Cancelled' }[loan.step];
}

/** Renew in Libby shows from `before` days ahead of the due date (board 4: 3 days). */
export function renewFrom(loan: Loan, before = 3): string | null {
  return loan.step === 'borrowed' && loan.due_on ? addDays(loan.due_on, -before) : null;
}

// ---------- Open in (carried over from today's app, v17.00, tested Oct 6–7) ----------

export interface OpenTarget {
  label: string;
  url: string;
  sub: string;
  /** The copy it opens (its card in Copies & formats has the same button). */
  copyId: string;
}

const asinOf = (s: string | null | undefined) => {
  const id = String(s ?? '').trim().replace(/[-\s]/g, '');
  return /^[A-Z0-9]{10}$/i.test(id) ? id.toUpperCase() : '';
};

const asinFromLink = (url: string | null) => asinInLink(url);

/** One Open in button per copy you can read now, in the copy order; a borrowed library Kindle wins over a store Kindle. */
export function openTargets(book: Pick<BookData, 'copies' | 'reads' | 'editions' | 'work' | 'authors'>, phone: boolean): OpenTarget[] {
  const out: (Omit<OpenTarget, 'copyId'> & { copyId: string; kind: string; lib: boolean })[] = [];
  const query = encodeURIComponent(`${book.work.title} ${book.authors[0] ?? ''}`.trim());
  for (const c of copiesInOrder(book).filter(readableNow)) {
    const e = editionOf(book, c);
    const lib = isLibrary(c);
    const copyId = c.id;
    const sub = lib ? `library copy${currentLoan(c)?.due_on ? ` · due ${dayText(currentLoan(c)!.due_on)}` : ''}` : 'your copy';
    if (c.format === 'kindle') {
      const id = (lib ? asinFromLink(c.library_kindle_url) : '') || asinOf(e?.asin);
      if (id) {
        out.push({
          kind: 'kindle',
          lib,
          copyId,
          label: 'Open in Kindle ↗',
          sub,
          url: phone
            ? `https://read.amazon.com/application/home/?_encoding=UTF8&action=open&asin=${id}&fallbackToAppDownloadPage=true`
            : `https://read.amazon.com/?asin=${id}`,
        });
      } else {
        out.push({ kind: 'kindle', lib, copyId, label: 'Open Kindle ↗', sub: `${sub} · no Kindle ID yet`, url: phone ? 'kindle://' : 'https://read.amazon.com/kindle-library' });
      }
    } else if (c.format === 'audible') {
      const id = asinOf(e?.audible_id);
      if (id) {
        out.push({
          kind: 'audible',
          lib,
          copyId,
          label: phone ? 'Open in Audible ↗' : 'Play in Audible ↗',
          sub,
          url: phone ? `https://www.audible.com/pd/${id}` : `https://www.amazon.com/arya/webplayer?marketplaceId=AF2M0KC94RCEA&asin=${id}`,
        });
      } else {
        out.push({ kind: 'audible', lib, copyId, label: 'Find in Audible ↗', sub: `${sub} · no Audible ID yet`, url: `https://www.audible.com/search?keywords=${query}` });
      }
    } else if (c.format === 'libby') {
      out.push({ kind: 'libby', lib, copyId, label: 'Open in Libby ↗', sub, url: 'https://libbyapp.com/shelf/loans' });
    }
  }
  const libKindle = out.some((t) => t.kind === 'kindle' && t.lib && t.label === 'Open in Kindle');
  const seen = new Set<string>();
  return out
    .filter((t) => !(libKindle && t.kind === 'kindle' && !t.lib))
    .filter((t) => (seen.has(t.url) ? false : (seen.add(t.url), true)))
    .map(({ label, url, sub, copyId }) => ({ label, url, sub, copyId }));
}

/** The book's title and first author, for a store or catalog search. */
const searchWords = (book: Pick<BookData, 'work' | 'authors'>) => `${book.work.title} ${book.authors[0] ?? ''}`.trim();

/** An ISBN-13 starting 978 as its ISBN-10 (Amazon's page address for a printed book). */
function isbn10Of(isbn13: string | null): string | null {
  if (!isbn13 || !/^978\d{10}$/.test(isbn13)) return null;
  const core = isbn13.slice(3, 12);
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += (10 - i) * Number(core[i]);
  const c = (11 - (sum % 11)) % 11;
  return core + (c === 10 ? 'X' : String(c));
}

/**
 * Amazon (Kristi, Oct 30; as today's app): the Kindle edition's page when the book has a Kindle ID,
 * else a printed edition's page from its ISBN, else an Amazon search for the title and author.
 */
export function amazonLink(book: Pick<BookData, 'work' | 'authors' | 'editions'>): string {
  const asin = book.editions.find((e) => e.kind === 'ebook' && e.asin && /^B[0-9A-Z]{9}$/.test(e.asin))?.asin;
  if (asin) return `https://www.amazon.com/dp/${asin}`;
  const isbn10 = book.editions.map((e) => (e.kind === 'print' ? isbn10Of(e.isbn13) : null)).find(Boolean);
  if (isbn10) return `https://www.amazon.com/dp/${isbn10}`;
  return `https://www.amazon.com/s?k=${encodeURIComponent(searchWords(book))}&i=stripbooks`;
}

/**
 * Library catalog (Kristi, Oct 30; as today's app): a search of your library's catalog for the
 * title and author. BiblioCommons catalogs (Multnomah's among them) live at <Libby code>.bibliocommons.com;
 * the catalog link in Settings › Lending library replaces this when that screen exists.
 */
export function catalogSearch(book: Pick<BookData, 'work' | 'authors' | 'libbyCode'>): string | null {
  if (!book.libbyCode) return null;
  return `https://${encodeURIComponent(book.libbyCode)}.bibliocommons.com/v2/search?query=${encodeURIComponent(searchWords(book))}&searchType=smart`;
}

export function libbySearch(book: Pick<BookData, 'work' | 'authors' | 'libbyCode'>): string | null {
  if (!book.libbyCode) return null;
  const q = encodeURIComponent(`${book.work.title} ${book.authors[0] ?? ''}`.trim());
  return `https://libbyapp.com/search/${encodeURIComponent(book.libbyCode)}/search/query-${q}/page-1`;
}

export const goodreadsLink = (id: string | null) => (id && /^\d+$/.test(id) ? `https://www.goodreads.com/book/show/${id}` : null);

/** The cover: the first copy (in order) whose edition has one, else any edition's. */
export function coverOf(book: Pick<BookData, 'copies' | 'reads' | 'editions'> & { coverLink?: string | null }): string | null {
  // Your own cover link first (edit mode); a Drive share link shows its picture (Oct 30), any
  // other Drive or Docs page is skipped.
  const own = ownCover(book.coverLink);
  if (own) return own;
  for (const c of copiesInOrder(book)) {
    const u = editionOf(book, c)?.cover_url;
    if (u) return u;
  }
  return book.editions.find((e) => e.cover_url)?.cover_url ?? null;
}

/** A summary's quiet source line (Kristi, Oct 26): only for a lookup's summary. */
export function summarySource(source: string | null): string | null {
  return source === 'Google Books' || source === 'Open Library' ? `From ${source}` : null;
}

// ---------- pills under the title ----------

/** Literary genre as the pill shows it (0024 added Poetry and Drama). */
export const LITERARY_GENRE_NAMES = { fiction: 'Fiction', nonfiction: 'Nonfiction', poetry: 'Poetry', drama: 'Drama' } as const;

export function statusPills(book: BookData, today: string): string[] {
  const pills: string[] = [statusName(book.status)];
  for (const c of copiesInOrder(book)) {
    const l = isLibrary(c) ? currentLoan(c) : undefined;
    if (l?.step === 'borrowed' && l.due_on) {
      // The library state's words (R2, L5): "Library Kindle · Due Oct 27", "· Due today", "· Loan ended Oct 28", "· Not started · due Nov 19".
      const state = loanState({ step: l.step, dueOn: l.due_on }, book.status === 'currently_reading', today);
      if (state) pills.push(`Library ${formatName(c, editionOf(book, c))} · ${stateWords(state, { dueOn: l.due_on }, today)}`);
    }
  }
  const own = [...new Set(book.copies.filter(isOwnNow).map((c) => `${formatName(c, editionOf(book, c))} · ${HOW_NAMES[c.how]}`))];
  if (own.length) pills.push(...own);
  else if (book.status !== 'reference') pills.push('Not owned yet');
  if (book.work.fiction) pills.push(LITERARY_GENRE_NAMES[book.work.fiction]);
  if (book.favoriteAt) pills.push('♥ Favorite');
  const pause = openPause(openRead(book));
  if (pause) pills.push(`Paused${pause.paused_on ? ` · ${dayText(pause.paused_on, today)}` : ''}`);
  if (book.tryAgainAt) {
    const { tries, lastStoppedAt } = triesSinceFinish(book.reads);
    pills.push(`↺ Try again${tries > 1 ? ` · ${tries} tries` : ''}${lastStoppedAt !== null ? ` · last stopped at ${Math.round(lastStoppedAt)}%` : ''}`);
  }
  if (book.readAgainAt) pills.push('↻ Read again');
  if (book.status === 'not_for_me' && book.notForMe.kind) {
    const where = book.notForMe.where === 'discover' ? ' · in Discover' : '';
    pills.push(`${book.notForMe.kind === 'abandoned' ? 'Abandoned' : 'Passed on'}${book.notForMe.on ? ` · ${fuzzyText(book.notForMe.on, 'day', null, today)}` : ''}${where}`);
  }
  return pills;
}

/**
 * The pills under the title in three rows, as in today's app (Kristi, Oct 28): (1) library
 * loans, when there are any; (2) your copies (and library copies you have or are waiting for),
 * then "Not owned yet" in a dashed pill; (3) the literary genre, the shelf (Move ▾), then the marks.
 */
export function statusPillRows(book: BookData, today: string): { library: string[]; copies: string[]; notOwned: boolean; literary: string | null; shelf: string; marks: string[] } {
  const all = statusPills(book, today);
  const library = all.filter((p) => p.startsWith('Library '));
  const own = [...new Set(book.copies.filter(isOwnNow).map((c) => `${formatName(c, editionOf(book, c))} · ${HOW_NAMES[c.how]}`))];
  const lib = [
    ...new Set(
      book.copies
        .filter((c) => isLibrary(c) && ['borrowed', 'ready', 'on_hold'].includes(currentLoan(c)?.step ?? ''))
        .map((c) => `${formatName(c, editionOf(book, c))} · Library`),
    ),
  ];
  const literary = book.work.fiction ? LITERARY_GENRE_NAMES[book.work.fiction] : null;
  const marks = all.filter((p) => /^(♥|Paused|↺|↻|Abandoned|Passed on)/.test(p));
  return { library, copies: [...own, ...lib], notOwned: !own.length && book.status !== 'reference', literary, shelf: all[0]!, marks };
}

// ---------- My history ----------

const HISTORY_WORDS: Record<string, string> = {
  added: 'Added',
  moved: 'Moved',
  passed_on: 'Passed on',
  brought_back: 'Brought back',
  started: 'Started',
  stopped: 'Stopped',
  finished: 'Finished',
  reread_started: 'Started a re-read',
  paused: 'Paused',
  resumed: 'Resumed',
  try_again_set: '↺ Try again set',
  try_again_cleared: '↺ Try again cleared',
  read_again_set: '↻ Read again set',
  read_again_cleared: '↻ Read again cleared',
  import_batch: 'Added from the sheet import',
  other: 'Changed',
};

/** A My history step in board 4's three columns: its name (a pill) and what else it says. */
export function historyParts(h: HistoryRow, readNumber: (id: string) => number | null): { name: string; detail: string } {
  const name = h.kind === 'import_batch' ? 'Added' : (HISTORY_WORDS[h.kind] ?? h.kind);
  const bits: string[] = [];
  if (h.kind === 'import_batch') bits.push('from the sheet import');
  if ((h.kind === 'started' || h.kind === 'finished' || h.kind === 'stopped') && h.read_id) {
    const n = readNumber(h.read_id);
    if (n) bits.push(`Read ${n}`);
  }
  if (h.kind === 'moved' || h.kind === 'brought_back') {
    if (h.from_status !== null || h.to_status !== null) bits.push(`${statusName(h.from_status)} → ${statusName(h.to_status)}`);
  } else if (h.to_status) bits.push(`to ${statusName(h.to_status)}`);
  return { name, detail: bits.join(' · ') };
}

export function historyStep(h: HistoryRow, readNumber: (id: string) => number | null): string {
  let what = HISTORY_WORDS[h.kind] ?? h.kind;
  if ((h.kind === 'started' || h.kind === 'finished' || h.kind === 'stopped') && h.read_id) {
    const n = readNumber(h.read_id);
    if (n) what += ` Read ${n}`;
  }
  if (h.from_status !== null || h.to_status !== null) {
    if (h.kind === 'moved' || h.kind === 'brought_back') what += ` · ${statusName(h.from_status)} → ${statusName(h.to_status)}`;
    else if (h.kind === 'added' && h.to_status) what += ` · to ${statusName(h.to_status)}`;
  }
  return what;
}

export const ADDED_HOW: Record<string, string> = {
  by_hand: 'by hand',
  discover: 'from Discover',
  import: 'from the sheet import',
  other: '',
};
